import React, {useMemo, useState} from 'react'

import {isArray, mapValues} from 'lodash'
import {palette} from 'styled-tools'
import {useRouter} from 'next/router'
import styled from 'styled-components'
import {ParsedUrlQuery} from 'querystring'
import {useLocalStorage, useMedia, useUpdateEffect} from 'react-use'
import {Container, media} from 'styled-bootstrap-grid'

import {H2} from '@festi/common/components/typography'
import {gridTheme} from '@festi/common/themes'
import {VariantList} from '@festi/common/api/rest'
import settings from '@festi/common/constants/settings'
import {AlgoliaSearchResults} from '@festi/common/api/algolia'

import {AlgoliaProvider} from './context/AlgoliaContext'
import CustomHits from './components/CustomHits'
import CustomPagination from './components/CustomPagination'
import SearchGridToggleGroup from './components/SearchGridToggleGroup'
import SelectSize, {resultsOptions} from './components/SelectSize'
import {GridType} from 'src/components/cards/redesign/NewProductCard'
import SortDropdown from './components/SortDropdown'
import SubCategoryList from './components/SubCategoryList'

const CatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding-block: 8px;

  ${media.md`
    padding-block: 12px;
  `}
`

const Title = styled(H2)`
  font-size: 24px;
  line-height: 0.9;

  span {
    font-size: 16px;
    font-weight: 500;
    color: ${palette('ui30Solid')};
  }

  ${media.md`
    font-size: 40px;

    span {
      font-size: 24px;
    }
  `}
`

const HeaderRightSectionWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  white-space: nowrap;
`

const DropdownWidth = styled.div`
  width: auto;

  ${media.md`
    width: 320px;
  `}
`

const SearchFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  padding-top: 24px;

  ${media.md`
    flex-direction: row;
  `}
`

const SizeWrapper = styled.div`
  width: 100%;

  ${media.md`
    max-width: 340px;
  `}
`

export interface Props {
  id?: number
  title: string
  description?: string
  attrNameMap: Record<string, string>
  initialData?: AlgoliaSearchResults<VariantList>
  initialQuery: ParsedUrlQuery
  isCategoryPage?: boolean
}

export default function CategoryResults({
  id,
  title,
  description,
  initialData,
  attrNameMap,
  initialQuery,
  isCategoryPage = false,
}: Props) {
  const [hitsPerPage, setHitsPerPage] = useLocalStorage<number>(
    'hitsPerPage',
    resultsOptions[0].value,
  )
  const [gridType, setGridType] = useLocalStorage<GridType>(
    'gridType',
    'normal',
  )
  const [numberOfHits, setNumberOfHits] = useState<number>(
    initialData?.nbHits || 0,
  )

  const {query} = useRouter()

  const isMobile = useMedia(`(max-width: ${gridTheme.breakpoints.md}px)`, false)
  const mapQuery = mapValues(query ?? initialQuery, (v) =>
    isArray(v) ? v[0] : v,
  )

  const categoriesId = id ? `categories:${id}` : ''
  const channelListPath = `${settings.channelListingPath}: true`
  const filters = [categoriesId, channelListPath].filter(Boolean).join(' AND ')
  const facetFilters = [`${settings.channelListingPath}:true`]

  const page = useMemo(() => {
    const parsedPage = parseInt(mapQuery?.page as string)

    if (isNaN(parsedPage)) {
      return 0
    }

    return Math.max(0, parsedPage - 1)
  }, [mapQuery?.page])

  useUpdateEffect(() => {
    if (isMobile) {
      setGridType('normal')
    }
  }, [isMobile])

  return (
    <AlgoliaProvider
      configure={{hitsPerPage, filters, page, facetFilters}}
      attrNameMap={attrNameMap}
      initialQuery={initialQuery}
    >
      <Container>
        <CatHeader>
          <Title>
            {title} {!!query?.q && ` fyrir „${query.q}“`}
            <span>({numberOfHits})</span>
          </Title>

          <HeaderRightSectionWrapper>
            <DropdownWidth>
              <SortDropdown iconOnly={isMobile} />
            </DropdownWidth>

            {!isMobile && (
              <SearchGridToggleGroup
                selected={gridType}
                handleChange={setGridType}
              />
            )}
          </HeaderRightSectionWrapper>
        </CatHeader>

        <SubCategoryList categoryId={id} />

        <CustomHits
          title={title}
          query={mapQuery.q}
          gridType={gridType}
          description={description}
          attrNameMap={attrNameMap}
          isCategoryPage={isCategoryPage}
          setNumberOfHits={setNumberOfHits}
        />

        <SearchFooter>
          <div>
            <CustomPagination page={page} />
          </div>

          <SizeWrapper>
            <SelectSize
              hitsPerPage={hitsPerPage}
              setHitsPerPage={setHitsPerPage}
            />
          </SizeWrapper>
        </SearchFooter>
      </Container>
    </AlgoliaProvider>
  )
}
